import { Form, Formik, Field } from "formik";
import React, { FC, useState } from "react";
import axios from "axios";
import { useCookies } from "react-cookie";
import { useNavigate } from "react-router-dom";
import style from "./style.module.css";
import { userSchema } from "../../schema";
import useSnackbar from "../../context/Snackbar/useSnackbar";
import { API_BASE } from "../../config/api";
import { AUTH_COOKIE_MAX_AGE_MS, preSessionPost } from "../../utils/api";
import BrandMark from "../../Components/BrandMark";


const AuthenticationPage: FC = () => {
  const snack = useSnackbar();
  const [, setCookies] = useCookies();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  return (
    <div className={style.container}>
      {/* Left brand panel */}
      <div className={style.leftPanel}>
        <div className={style.brandSection}>
          <div className={style.brandLogoCircle}><BrandMark variant="dark" size={36} /></div>
          <h1 className={style.brandTitle}>1Markaz</h1>
          <p className={style.brandSubtitle}>Point of Sale Management System</p>
          <div className={style.brandFeatures}>
            <div className={style.brandFeature}><span className={style.featureDot} /> Manage stores, shops &amp; products</div>
            <div className={style.brandFeature}><span className={style.featureDot} /> Real-time inventory tracking</div>
            <div className={style.brandFeature}><span className={style.featureDot} /> Orders, reports &amp; analytics</div>
            <div className={style.brandFeature}><span className={style.featureDot} /> Role-based access control</div>
          </div>
        </div>
      </div>

      {/* Right form panel */}
      <div className={style.rightPanel}>
      <Formik
        validateOnChange={true}
        validateOnBlur={true}
        onSubmit={(values, { setSubmitting }) => {
          // preSessionPost (skipAuthRedirect): tells the shared
          // 401-interceptor (utils/api.ts) that a wrong-password 401 here
          // is a normal, expected response with no prior session to
          // expire — not a signal to attempt a silent refresh and
          // force-reload. withCredentials lets the backend set the
          // httpOnly refresh-token cookie.
          preSessionPost(`${API_BASE}/user/login`, {
              email: values.email,
              password: values.password,
            }, { withCredentials: true })
            .then((res) => {
              snack.onResponse({
                message: res.data.message || "Login successful!",
                status: 200,
              });

              // Store both user data and the short-lived access token
              // (the refresh token itself stays server-side in an httpOnly cookie)
              const authData = {
                ...res.data.user,
                accessToken: res.data.accessToken
              };

              setCookies("auth", authData, {
                expires: new Date(Date.now() + AUTH_COOKIE_MAX_AGE_MS),
              });

              // Set access token for API calls
              axios.defaults.headers.common['Authorization'] = `Bearer ${res.data.accessToken}`;
              setSubmitting(false);
              // replace, not push: keeps the login page itself out of
              // history as a back-target once signed in — see Sidebar's
              // logout navigate() for the matching half of this fix.
              navigate("/dashboard", { replace: true });
            })
            .catch((err) => {
              console.error('Login error:', err);
              console.error('Error response:', err.response?.data);
              console.error('Error status:', err.response?.status);
              
              let errorMessage = "Login failed";
              if (err.response?.data?.message) {
                errorMessage = err.response.data.message;
              } else if (err.response?.status === 401) {
                errorMessage = "Invalid username or password";
              } else if (err.response?.status === 403) {
                errorMessage = "Access denied. Only Admin users can access POS system.";
              } else if (err.response?.status === 400) {
                errorMessage = "Username and password are required";
              } else if (err.code === 'ECONNREFUSED') {
                errorMessage = "Cannot connect to server. Please check if backend is running.";
              }
              
              setSubmitting(false);
              snack.onResponse({
                message: errorMessage,
                status: err.response?.status || 500,
              });
            });
        }}
        initialValues={{
          email: "",
          password: "",
        }}
        validationSchema={userSchema}
      >
        {({ errors, touched }) => (
          <Form className={style.form} autoComplete="off">
            <h1 className={style.formTitle}>Welcome back</h1>
            <p className={style.formSubtitle}>Sign in to your POS account</p>
            <label htmlFor="email">Email</label>
            <Field
              name="email"
              id="email"
              type="email"
              placeholder="Enter Email"
              autoComplete="off"
              className={`${style.input} ${errors.email && touched.email ? style.inputError : ''}`}
            />
            {errors.email && touched.email && (
              <div className={style.errorMessage}>{errors.email}</div>
            )}
            <label htmlFor="password">Password</label>
            <div className={style.passwordContainer}>
              <Field
                name="password"
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="Enter Password"
                autoComplete="new-password"
                className={`${style.input} ${errors.password && touched.password ? style.inputError : ''}`}
              />
              <button
                type="button"
                className={style.eyeButton}
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                )}
              </button>
            </div>
            {errors.password && touched.password && (
              <div className={style.errorMessage}>{errors.password}</div>
            )}
            <button type="submit" className={style.button}>Login</button>
            <button
              type="button"
              className={style.forgotPasswordLink}
              onClick={() => navigate("/forgot-password")}
            >
              Forgot password?
            </button>
            {process.env.NODE_ENV === "development" && (
              <button
                type="button"
                style={{ marginTop: "1rem", background: "#f1f5f9", color: "#047857", border: "1px dashed #059669", padding: "10px", borderRadius: "6px", cursor: "pointer", fontWeight: "600", width: "100%" }}
                onClick={() => {
                  setCookies("auth", { user_id: 1, user_type: "O", first_name: "Demo", last_name: "Cashier", accessToken: "demo-token" }, { path: "/", expires: new Date(Date.now() + 86400000) });
                  navigate("/checkout", { replace: true });
                }}
              >
                ⚡ Quick Demo Login (Test Mode)
              </button>
            )}
          </Form>
        )}
      </Formik>
      </div>
    </div>
  );
};

export default AuthenticationPage;
